import React, { useState } from 'react';
import {
    Paper,
    Typography,
    Box,
    TextField,
    Button,
    IconButton,
    InputAdornment,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
} from '@mui/material';
import {
    ContentCopy as CopyIcon,
    Refresh as RefreshIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Settings as SettingsIcon,
} from '@mui/icons-material';
import { useToast } from '../toast/useToast';
import {
    useMqttCredentialsQuery,
    useRegenerateMqttPasswordMutation,
    useShowMqttPasswordMutation,
} from '../../api/JsdhNotifikaceService/service/IotMutations';

const MqttSettingsSection: React.FC = () => {
    const { showToast } = useToast();
    const [showPassword, setShowPassword] = useState(false);
    const [currentPassword, setCurrentPassword] = useState<string | null>(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    const { data: credentials, isLoading } = useMqttCredentialsQuery();
    const regenerateMutation = useRegenerateMqttPasswordMutation();
    const showPasswordMutation = useShowMqttPasswordMutation();

    const handleShowPassword = () => {
        showPasswordMutation.mutate(undefined, {
            onSuccess: (data) => {
                if (data.password) {
                    setCurrentPassword(data.password);
                    setShowPassword(true);
                } else {
                    showToast(
                        'Heslo zatím nebylo vygenerováno — použijte „Regenerovat heslo" (u nové jednotky jednorázově).',
                        'warning',
                    );
                }
            },
        });
    };

    const handleCopy = async (text: string, label: string) => {
        try {
            await navigator.clipboard.writeText(text);
            showToast(`${label} zkopírováno do schránky`, 'success');
        } catch {
            showToast('Nepodařilo se zkopírovat do schránky', 'error');
        }
    };

    const handleRegenerateClick = () => {
        setShowConfirmDialog(true);
    };

    const handleConfirmRegenerate = () => {
        setShowConfirmDialog(false);
        regenerateMutation.mutate(undefined, {
            onSuccess: (data) => {
                if (data.password) {
                    setCurrentPassword(data.password);
                    setShowPassword(true);
                }
            },
        });
    };

    if (isLoading) {
        return (
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            </Paper>
        );
    }

    return (
        <>
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <SettingsIcon color="primary" />
                    <Typography variant="h6">MQTT Nastavení</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Alert severity="info" sx={{ mb: 2 }}>
                    MQTT credentials pro připojení zařízení (Shelly / Raspberry Pi) k serveru.
                    Pro přidání dalšího senzoru zobrazte stávající heslo tlačítkem „Zobrazit heslo" —
                    <strong> regeneraci použijte jen když chcete heslo opravdu změnit</strong> (rozbije
                    senzory se starým heslem).
                </Alert>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                        label="MQTT Broker"
                        value={credentials?.brokerUrl || ''}
                        fullWidth
                        disabled
                        size="small"
                        helperText="V zařízení (Shelly) zadejte tento server a zapněte SSL/TLS"
                        InputProps={{
                            endAdornment: credentials?.brokerUrl && (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => handleCopy(credentials.brokerUrl, 'Broker URL')}
                                        size="small"
                                    >
                                        <CopyIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    <TextField
                        label="MQTT Username"
                        value={credentials?.username || ''}
                        fullWidth
                        disabled
                        size="small"
                        InputProps={{
                            endAdornment: credentials?.username && (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => handleCopy(credentials.username, 'Username')}
                                        size="small"
                                    >
                                        <CopyIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <TextField
                            label="MQTT Password"
                            value={
                                currentPassword
                                    ? showPassword
                                        ? currentPassword
                                        : '••••••••••••'
                                    : 'Heslo je skryto'
                            }
                            fullWidth
                            disabled
                            size="small"
                            InputProps={{
                                endAdornment: currentPassword && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            size="small"
                                        >
                                            {showPassword ? (
                                                <VisibilityOffIcon fontSize="small" />
                                            ) : (
                                                <VisibilityIcon fontSize="small" />
                                            )}
                                        </IconButton>
                                        <IconButton
                                            onClick={() => handleCopy(currentPassword, 'Heslo')}
                                            size="small"
                                        >
                                            <CopyIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Button
                                onClick={handleShowPassword}
                                variant="outlined"
                                startIcon={
                                    showPasswordMutation.isPending ? (
                                        <CircularProgress size={16} color="inherit" />
                                    ) : (
                                        <VisibilityIcon />
                                    )
                                }
                                disabled={showPasswordMutation.isPending}
                                sx={{ whiteSpace: 'nowrap', minWidth: 220, fontWeight: 600 }}
                            >
                                Zobrazit heslo
                            </Button>
                            <Button
                                onClick={handleRegenerateClick}
                                variant="contained"
                                color="warning"
                                startIcon={
                                    regenerateMutation.isPending ? (
                                        <CircularProgress size={16} color="inherit" />
                                    ) : (
                                        <RefreshIcon />
                                    )
                                }
                                disabled={regenerateMutation.isPending}
                                sx={{
                                    whiteSpace: 'nowrap',
                                    minWidth: 220,
                                    fontWeight: 600,
                                    boxShadow: 1,
                                    '&:hover': {
                                        boxShadow: 2,
                                    },
                                }}
                            >
                                Regenerovat heslo
                            </Button>
                        </Box>
                    </Box>

                    {currentPassword && (
                        <Alert severity="success">
                            Heslo zkopírujte do detektoru (username + heslo + adresa brokeru + prefix
                            <code> shelly/{credentials?.username}/o&#123;objektId&#125;/&#123;shellyId&#125;</code>).
                            Toto stejné heslo můžete kdykoli znovu zobrazit — nemusíte regenerovat.
                        </Alert>
                    )}
                </Box>
            </Paper>

            {/* Potvrzovací dialog pro regeneraci hesla — důrazné varování */}
            <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)}>
                <DialogTitle>Opravdu regenerovat MQTT heslo?</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mt: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Vygeneruje se NOVÉ heslo.{' '}
                            {credentials?.activeSensorCount
                                ? `Všech ${credentials.activeSensorCount} aktivních senzorů`
                                : 'Všechny senzory'}{' '}
                            se stávajícím heslem PŘESTANE fungovat a musíte je ručně překonfigurovat
                            (u Shelly přepsat heslo ve webovém rozhraní).
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 600 }}>
                            Chcete jen přidat další senzor? NEREGENERUJTE — zavřete tento dialog a použijte
                            „Zobrazit heslo".
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }}>
                            (Raspberry Pi s watchdogem si nové heslo stáhnou automaticky; staré platí pro
                            obnovu 24 h. Shelly se neobnovuje samo.)
                        </Typography>
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowConfirmDialog(false)} variant="contained" autoFocus>
                        Zrušit
                    </Button>
                    <Button
                        onClick={handleConfirmRegenerate}
                        color="error"
                    >
                        Ano, regenerovat
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default MqttSettingsSection;
